<template>
    <div v-if="loaded">

        <rkm-row class="mt-1">
            <rkm-col>
                <div class="aviso-lei">
                    <p>
                        As informações de estoque de medicamentos e materiais aqui apresentadas atendem à 
                        <strong>Lei Federal nº 14.654/2023</strong>, que determina a divulgação pública e periódica 
                        dos estoques das unidades de saúde do SUS. Os dados são atualizados regularmente para 
                        garantir transparência, controle social e acesso facilitado ao cidadão.
                    </p>
                    <p>
                        Dúvidas ou solicitações adicionais podem ser encaminhadas à gestão municipal de saúde.
                    </p>
                </div>
            </rkm-col>
        </rkm-row>
        
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            label="Unidade"
                            default-option-text="Todos"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                     <rkm-col cols="4">
                        <rkm-form-input-autocomplete
                            :repository="repositories.relatorioPosicaoEstoque.material"
                            :request-query-string-params="autocompleteQueryStringParams.material"
                            :search-fields="autocompleteFields.material"
                            text-field="descricao"
                            label="Medicamento / Material"
                            uppercase
                            v-model="model.material"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                @sort-changed="onSortChanged"
                :perPage="50"
            >

                <template v-slot:thead-top>
                    <rkm-tr class="estoque-header">
                        <rkm-th :colspan="fields.length - 3"></rkm-th>
                        <rkm-th class="text-right" colspan="4">
                            <div class="estoque-real-info" style="display: flex; justify-content: space-between; width: 100%;">
                                <span class="estoque-label">Estoque em tempo Real</span>
                                <span class="estoque-date">Atualizado em: {{ dataAtual }}</span>
                            </div>
                        </rkm-th>
                    </rkm-tr>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { data, error, file, object, repository, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        autocompleteQueryStringParams() {

            const params = this.prepareData()

            delete params.material_id

            return {
                material: { ...params, customer: this.customer }
            }
        },
        autocompleteFields() {

            return {
                material: [
                    { key: 'codigo_material', searchable: true },
                    { key: 'descricao', searchable: true },
                ],
            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {

            return {
                unidade: { required: true },
                infraestrutura: { required: true },
                centroCusto: { required: true },
            }
        },
        disableInfraestrutura() {

            return this.model.unidadeId === null
        },
        disableCentroCusto() {

            return this.model.infraestruturaId === null
        },
        disableGrupo() {

            return this.model.centroCustoId === null
        },
        disableSubgrupo() {

            return this.model.grupoId === null
        },
    },
    created() {

        this.repositories = {
            impressao: this.getConectaSusRepository('portalDivulgacao.imprimirRelatorioPosicaoEstoque'),
            relatorioPosicaoEstoque: this.getConectaSusRepository('portalDivulgacao.relatorioPosicaoEstoque'),
            cliente: this.getConectaSusRepository('intranet.cliente'),
        }
    },
    data() {

        return {
            loaded: false,
            dataAtual: null,
            customer: null,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: false,
            indeterminate: true,
            selectedCols: [
                'grupo',
            ],
            columns: [
                { key: 'materialDescricao', label: 'Medicamento', sortable: true, fixed: true },
                { key: 'unidade', label: 'Unidade', sortable: true, fixed: true },
                { key: 'apresentacao', label: 'Apresentação', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'estoqueDisponivel', label: 'Saldo em estoque', class: 'col-min text-center', sortable: true, fixed: true },
            ],
            itens: [],
            sortBy: 'materialDescricao',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading', 'clear', 'setCustomer']),
        load(customer) {

            this.repositories.cliente.index({link: customer})
                .then(({ data }) => {
                    this.setCustomer(data)
                    this.list(customer)
                })
                .catch(this.handleError)
                .finally()

        },
        list(customer){

            this.setLoading()

            this.repositories.relatorioPosicaoEstoque.list({customer: customer})
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.loaded = true

                    
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading(this.search()))

        },
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioPosicaoEstoque.list({ ...this.prepareData(), load_from: this.camelToSnake(field), customer: this.customer })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field != 'material') {

                field = `${field}Id`
            }

            const fields = [
                'unidadeId',
                'infraestruturaId',
                'centroCustoId',
                'grupoId',
                'subgrupoId',
                'material',
            ]

            fields.slice(fields.indexOf(field) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            this.repositories.relatorioPosicaoEstoque.index({...this.prepareData(), customer: this.customer})
                .then(({ data }) => {

                    this.itens = data.materiais
                    this.dataAtual = data.dataAtual

                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            const data = this.prepareDataWithColumns();

            data.sortBy = this.sortBy
            data.sortDesc = this.sortDesc

            this.repositories.impressao.imprimirRelatorioPosicaoEstoque({...data, customer: this.customer})
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.createIdKeysToObject(data, ['material'])

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mounted() {
        const env = process.env.VUE_APP_ENV
        let customer = null

        if (env === 'production') {
            customer = window.location.hostname.split('.')[0]
        } else {
            customer = this.$route.params.customer
        }

        this.customer = customer

        if(customer) {
            this.clear().then(() => {
                this.load(customer)
            });
        }
    },
    mixins: [data, error, file, object, repository, stringCase],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    },
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}

.estoque-real-info {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.aviso-lei {
    background-color: #f8f9fa; 
    border-left: 4px solid #0062cc; 
    padding: 12px 16px;
    margin: 10px 0;
    border-radius: 6px;
    font-size: 0.95rem;
    line-height: 1.5;
    color: #333;
}

.aviso-lei p {
    margin: 4px 0;
}

.aviso-lei strong {
    color: #0062cc; 
}
</style>